// lib
import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import { MAPPING_HELP, MAPPING_BUTTON_HELP } from '@aytm/app/stats/page/sidebar/components/lib/helps'
import { MAPPING_FEATURES, MAPPING_PUBLISH_FEATURES } from '@aytm/app/stats/page/sidebar/components/lib/constants'

import { collator } from '@aytm/app/stats/lib/util/helpers'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { isReportStorageDisabled } from '@aytm/app/stats/lib/stat-page'
import { GetDataByKey } from '@aytm/lib/storage/index.js'

import useEventListener from '@aytm/app/stats/page/components/hooks/useEventListener'

// components
import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'

import DropTargetContainer from '@aytm/app/stats/page/sidebar/components/questions/DropTargetContainer'

import CustomQuestionButton from '@aytm/app/stats/page/sidebar/components/questions/CustomQuestionButton'
import PinnedCustomQuestion from '@aytm/app/stats/page/sidebar/components/questions/PinnedCustomQuestion'

import SidebarPopupWarning from '@aytm/app/stats/page/sidebar/components/SidebarPopupWarning'

// styles
import style from '@aytm/app/stats/page/sidebar/mapping/styles/MappingPanel.module.scss'

const MappingPanel = ({
  sidebar,
  model,
  features,
  addQuestion,
  addPinnedQuestion,
  removePinnedQuestion,
  onModeChange,
  onDNDStart,
  onDNDEnd
}) => {
  const { mappingButtons: buttons } = model

  const disabled =
    'charts' != model.mode || model.isReportVirtual || model.isReportXpert || features.reorder_questions.disabled

  const [mappings, setMappings] = useState([])
  const [questions, setQuestions] = useState([])

  const componentRef = useRef(null)

  const [root, setRoot] = useState(null)

  const setComponentRef = useCallback(node => {
    componentRef.current = node
    setRoot(node?.parentNode ?? null)
  }, [])

  // Where the report cannot be stored nothing can be pinned, so no pin drop is offered at all
  const pinDisabled = isReportStorageDisabled()
  const acceptTypes = useMemo(
    () =>
      pinDisabled
        ? []
        : buttons.reduce((acc, cur) => {
            if (!features[cur.feature].disabled && !features[`${cur.feature}_publish`].disabled) acc.push(cur.adndType)
            return acc
          }, []),
    [buttons, features, pinDisabled]
  )

  const typesTitles = useMemo(
    () =>
      buttons.reduce((acc, cur) => {
        if (!features[cur.feature].disabled && !features[`${cur.feature}_publish`].disabled)
          acc[cur.adndType] = cur.text
        return acc
      }, {}),
    [buttons, features]
  )

  const getLocalStorageData = useCallback(
    () =>
      GetDataByKey('AYTM-Reports', `${PageGlobals.SURVEY_ID}${PageGlobals.USER_ID || 0}pquestions`).then(data =>
        setQuestions(
          null !== data
            ? data.reduce((acc, cur, index) => {
                if (
                  !['dld', 'dlt'].includes(cur.type) ||
                  !Object.hasOwn(cur, 'features') ||
                  !cur.features.insightsMapping
                ) {
                  return acc
                }
                if (
                  (features.data_lab_concept.disabled || features.data_lab_concept_publish.disabled) &&
                  'dld' === cur.type
                ) {
                  return acc
                }
                if (
                  (features.data_lab_tracker.disabled || features.data_lab_tracker_publish.disabled) &&
                  'dlt' === cur.type
                ) {
                  return acc
                }
                const data = JSON.parse(JSON.stringify(cur))
                data.features || (data.features = { insightsMapping: true })
                data.variables || (data.variables = [])
                acc.push({
                  type: cur.type,
                  text: cur.text,
                  icon: PageGlobals.QUESTION_TYPE_ICONS[`${cur.type}-mapping`],
                  data: data,
                  index: index
                })
                return acc
              }, [])
            : []
        )
      ),
    // Spread the disabled booleans so the deps are stable primitives (not fresh array literals each render).
    [...MAPPING_FEATURES, ...MAPPING_PUBLISH_FEATURES].map(feature => features[feature].disabled)
  )

  useEffect(() => {
    const items = model.reports.flatMap(report => {
      const virtual = Object.values(report.current.virtual || {}).filter(
        q =>
          ('dld' === q.type && !features.data_lab_concept.disabled && !features.data_lab_concept_publish.disabled) ||
          ('dlt' === q.type && !features.data_lab_tracker.disabled && !features.data_lab_tracker_publish.disabled)
      )
      virtual.sort((a, b) => collator.compare(a.id, b.id))
      return virtual.reduce((acc, cur) => {
        const data = JSON.parse(JSON.stringify(cur))
        data.features ||
          (data.features = {
            insightsMapping: true
          })
        data.variables || (data.variables = [])
        data.entities = data.entities.map(function (item) {
          delete item.options
          return item
        })
        data.attributes = data.attributes.map(function (item) {
          delete item.options
          return item
        })
        data.variables = data.variables.map(function (item) {
          delete item.options
          return item
        })
        delete data.id
        acc.push({
          key: `${report.id}-${cur.id}`,
          type: cur.type,
          text: cur.text,
          icon: PageGlobals.QUESTION_TYPE_ICONS[`${cur.type}-mapping`],
          data: data
        })
        return acc
      }, [])
    })

    // Rebuild from the current report virtuals: reconciles inserts, reorders, deletes, and tail-truncation.
    setMappings(items)
  }, [
    JSON.stringify(model.reports.map(report => report.current.virtual)),
    ...[...MAPPING_FEATURES, ...MAPPING_PUBLISH_FEATURES].map(feature => features[feature].disabled)
  ])

  useEffect(() => getLocalStorageData(), [getLocalStorageData])

  useEventListener('pquestionsLocalChangesChanged', () => getLocalStorageData())

  return (
    <>
      <SidebarPopupWarning
        features={
          // reorder_questions.disabled folds in mode/report/Xpert/QA, so it drives the disabled-overlay variant
          disabled
            ? [features['reorder_questions']]
            : MAPPING_FEATURES.map(feature => features[feature]).filter(item => !item.hidden)
        }
        isSpecial={'charts' != model.mode}
        isReportVirtual={model.isReportVirtual}
        isReportXpert={model.isReportXpert}
        onModeChange={onModeChange}
      />

      <div ref={setComponentRef} className={style.component}>
        <div className={style['component-title']}>
          Insights Portal Mapping
          {root && <SidebarPopup id="mapping" root={root} help={MAPPING_HELP} width="300px" />}
        </div>

        <DropTargetContainer
          root={sidebar}
          acceptTypes={acceptTypes}
          typesTitles={typesTitles}
          onDNDDrop={addPinnedQuestion}
        >
          <div className={`${style.row} ${style.paragraph}`}>
            Add data from this survey to Insights Portal by creating a new mapping or editing an existing mapping. You
            can send concepts, attributes, and variables to your portals.
          </div>

          <div className={style.section}>
            <div className={style['section-title']}>Create new mapping</div>

            {buttons.map(item =>
              features[item.feature].hidden ? null : (
                <CustomQuestionButton
                  dataTestID={`stat-mapping-${item.id}`}
                  key={item.type}
                  root={root}
                  item={{ ...item, help: MAPPING_BUTTON_HELP[item.id] || '' }}
                  features={features}
                  disabled={features[item.feature].disabled || features[`${item.feature}_publish`].disabled || disabled}
                  addQuestion={addQuestion}
                  onDNDStart={onDNDStart}
                  onDNDEnd={onDNDEnd}
                />
              )
            )}
          </div>

          {0 < mappings.length && (
            <div className={style.section}>
              <div className={style['section-title']}>Saved mappings</div>

              <div className={`${style.row} ${style.note}`}>
                Note: if a mapping is deleted from the report it&apos;s saved in, it will also be permanently removed
                from this list of saved mappings.
              </div>

              {mappings.map(item => (
                <PinnedCustomQuestion
                  key={item.key}
                  item={item}
                  disabled={disabled}
                  addQuestion={addQuestion}
                  onDNDStart={onDNDStart}
                  onDNDEnd={onDNDEnd}
                />
              ))}
            </div>
          )}

          {0 < questions.length && !disabled ? (
            <div className={style.section}>
              <div className={style['section-title']}>Pinned mappings</div>

              {questions.map(item => (
                <PinnedCustomQuestion
                  key={`question-${item.index}`}
                  item={item}
                  addQuestion={addQuestion}
                  removePinnedQuestion={removePinnedQuestion}
                  onDNDStart={onDNDStart}
                  onDNDEnd={onDNDEnd}
                />
              ))}
            </div>
          ) : null}
        </DropTargetContainer>
      </div>
    </>
  )
}

MappingPanel.propTypes = {
  sidebar: PropTypes.any,
  root: PropTypes.any,
  model: PropTypes.object,
  features: PropTypes.object,
  addQuestion: PropTypes.func,
  addPinnedQuestion: PropTypes.func,
  removePinnedQuestion: PropTypes.func,
  onModeChange: PropTypes.func,
  onDNDStart: PropTypes.func,
  onDNDEnd: PropTypes.func
}

MappingPanel.displayName = 'MappingPanel'

export default MappingPanel
